"use client";

import { useEffect, useRef } from "react";
import { useFiltersStore } from "@/stores/filtersStore";
import { useAriaAnnouncerStore } from "@/stores/ariaAnnouncerStore";

/**
 * Hook that announces filter changes to screen readers and shows toast notifications.
 * Tracks filter additions and removals and creates human-readable announcements.
 */
export const useFilterAnnouncements = () => {
  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const searchText = useFiltersStore((state) => state.searchText);
  const { announce } = useAriaAnnouncerStore((state) => state.actions);

  const previousFiltersRef = useRef<string>("");
  const previousSearchTextRef = useRef<string>("");
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousFiltersRef.current = JSON.stringify(activeFilters);
      previousSearchTextRef.current = searchText;
      return;
    }

    const currentFiltersKey = JSON.stringify(activeFilters);
    const filtersChanged = currentFiltersKey !== previousFiltersRef.current;
    const searchChanged = searchText !== previousSearchTextRef.current;

    if (filtersChanged || searchChanged) {
      const announcements: string[] = [];

      if (searchChanged) {
        if (searchText) {
          announcements.push(`Searching for "${searchText}"`);
        } else {
          announcements.push("Search cleared");
        }
      }

      const filterCount = Object.entries(activeFilters)
        .filter(([key]) => key !== "id")
        .reduce((total, [key, value]) => {
          if (key === "available" || key === "currency" || key === "sort") return total;
          return total + (Array.isArray(value) ? value.length : 0);
        }, 0);

      if (filtersChanged && !searchChanged) {
        if (filterCount > 0) {
          announcements.push(`${filterCount} filter${filterCount !== 1 ? "s" : ""} applied`);
        } else {
          announcements.push("Filters cleared");
        }
      }

      if (announcements.length > 0) {
        announce(announcements.join(", "));
      }
    }

    previousFiltersRef.current = currentFiltersKey;
    previousSearchTextRef.current = searchText;
  }, [activeFilters, searchText, announce]);
};
